/**
 * Balance & Usage API Client
 * 
 * Client for interacting with the balance and usage tracking API.
 */

import { getJWTToken } from '../auth/token-storage.js';
import { log } from '../logging/index.js';
import { handleFetchError, handleHTTPError } from '../error-handler/index.js';

// Cortex API runs on port 8787 locally (see cortex-api/wrangler.toml)
const CORTEX_API_BASE_URL = 
  import.meta.env.DEV 
    ? 'http://localhost:8787' 
    : 'https://pivot-is-cortex-api.dont-d23.workers.dev';

export interface Balance {
  balanceCents: number;
  balanceCredits: number; // Same as balanceCents (1 credit = 1 cent)
  reservedCents?: number; // Credits currently reserved (not yet committed)
  availableCents?: number; // Available credits = balanceCents - reservedCents
  plan: string;
  autoRechargeEnabled: boolean;
  autoRechargeThresholdCents: number;
}

export interface BalanceResponse {
  success: boolean;
  data?: Balance;
  error?: string;
}

export interface UsageEvent {
  id: string;
  eventType: string;
  resourceId?: string;
  quantity: number;
  costCents: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

export interface UsageHistoryResponse {
  success: boolean;
  data?: {
    events: UsageEvent[];
    totalCostCents: number;
    totalEvents: number;
  };
  error?: string;
}

export interface CheckBalanceResponse {
  success: boolean;
  data?: {
    hasEnough: boolean;
    currentBalanceCents: number;
    requiredCents: number;
    shortfallCents?: number;
  };
  error?: string;
}

/**
 * Get current balance
 */
export async function getBalance(): Promise<BalanceResponse> {
  const token = getJWTToken();
  if (!token) {
    return {
      success: false,
      error: 'Authentication required'
    };
  }

  try {
    const response = await fetch(`${CORTEX_API_BASE_URL}/api/balance`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorMessage = await handleHTTPError(response, 'Balance API');
      return {
        success: false,
        error: errorMessage || `HTTP ${response.status}`
      };
    }

    return await response.json();
  } catch (error) {
    handleFetchError(error, 'Balance API');
    log.error('[BalanceAPI] Error getting balance:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error'
    };
  }
}

/**
 * Get usage history
 */
export async function getUsageHistory(limit: number = 100, offset: number = 0): Promise<UsageHistoryResponse> {
  const token = getJWTToken();
  if (!token) {
    return {
      success: false,
      error: 'Authentication required'
    };
  }

  try {
    const response = await fetch(`${CORTEX_API_BASE_URL}/api/usage?limit=${limit}&offset=${offset}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorMessage = await handleHTTPError(response, 'Usage API');
      return {
        success: false,
        error: errorMessage || `HTTP ${response.status}`
      };
    }

    return await response.json();
  } catch (error) {
    handleFetchError(error, 'Usage API');
    log.error('[UsageAPI] Error getting usage history:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error'
    };
  }
}

/**
 * Check if user has enough credits for an operation
 */
export async function checkBalance(
  resourceType: string,
  resourceId: string,
  quantity: number,
  tier: string = 'free'
): Promise<CheckBalanceResponse> {
  const token = getJWTToken();
  if (!token) {
    return {
      success: false,
      error: 'Authentication required'
    };
  }

  try {
    const response = await fetch(`${CORTEX_API_BASE_URL}/api/balance/check`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ resourceType, resourceId, quantity, tier })
    });

    if (!response.ok) {
      const errorMessage = await handleHTTPError(response, 'Balance API');
      return {
        success: false,
        error: errorMessage || `HTTP ${response.status}`
      };
    }

    return await response.json();
  } catch (error) {
    handleFetchError(error, 'Balance API');
    log.error('[BalanceAPI] Error checking balance:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error'
    };
  }
}

